import { useCallback, useEffect, useRef, useState } from 'react';
import type { FormEvent, JSX } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Badge, Button, SidePanel } from '@silverfalls/sterling';
import {
  fetchReceipt,
  receiptAttachmentDownloadUrl,
  reverseGoodsReceipt,
  type ReceiptRow,
} from '../api/p2p.js';
import { DetailRow } from '../components/DetailRow.js';
import { useToast } from '../components/Toast.js';
import { formatDateTime, formatRelativeDate } from '../lib/format.js';

const REQUESTING_USER = 'u-ui-skeleton';

export function ReceiptDetail(): JSX.Element {
  const { ref } = useParams<{ ref: string }>();
  const toast = useToast();
  const [receipt, setReceipt] = useState<ReceiptRow | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reverseOpen, setReverseOpen] = useState(false);

  const load = useCallback(async () => {
    if (!ref) return;
    setLoading(true);
    setError(null);
    try {
      const row = await fetchReceipt(ref);
      setReceipt(row);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [ref]);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading) return <div className="t-body-sm">Loading receipt…</div>;
  if (error) {
    return (
      <div className="alert alert-neg" role="alert">
        Couldn't load receipt: {error}
      </div>
    );
  }
  if (!receipt) {
    return (
      <div className="empty-state">
        <p>Receipt not found: {ref}</p>
        <p>
          <Link to="/receipts">Back to receipts</Link>
        </p>
      </div>
    );
  }

  const totalQty = receipt.lines.reduce((sum, l) => sum + Number(l.receivedQuantity), 0);

  return (
    <section className="vendor-detail">
      <div className="screen-header">
        <div className="entity-header">
          <Link to="/receipts" className="back-link">
            ← Receipts
          </Link>
          <h2 className="mono">{receipt.receiptRef.slice(0, 24)}…</h2>
          <div className="entity-ref mono">{receipt.receiptRef}</div>
          <div className="entity-summary">
            <span className="entity-summary-part">
              <Badge variant="success" dot={false}>{receipt.state}</Badge>
            </span>
            <span className="entity-summary-part">
              <Badge variant={receipt.kind === 'SERVICE' ? 'info' : 'success'} dot={false}>
                {receipt.kind}
              </Badge>
            </span>
            <span className="entity-summary-part">
              <Link to={`/purchase-orders/${receipt.poRef}`} className="mono">
                {receipt.poRef.slice(0, 16)}…
              </Link>
            </span>
            <span className="entity-summary-part mono">
              {receipt.lines.length} lines · {totalQty.toFixed(2)} units
            </span>
            <span className="entity-summary-part mono">
              {formatRelativeDate(receipt.receivedAt)}
            </span>
            {receipt.reversedAt && (
              <span className="entity-summary-part">
                <Badge variant="warning" dot={false}>REVERSED</Badge>
              </span>
            )}
          </div>
        </div>
        <div className="header-right-stack">
          <div className="state-actions">
            {!receipt.reversedAt && (
              <Button
                variant="destructive"
                size="sm"
                onClick={() => setReverseOpen(true)}
              >
                Reverse receipt
              </Button>
            )}
          </div>
        </div>
      </div>

      {receipt.reversedAt && (
        <div className="alert alert-warn" role="status">
          <strong>This receipt is reversed.</strong>{' '}
          {receipt.reversedBy && (
            <>
              by <span className="mono">{receipt.reversedBy}</span>
            </>
          )}
          {' '}at <span className="mono">{formatDateTime(receipt.reversedAt)}</span>
          {receipt.reversalReason && <> — {receipt.reversalReason}</>}
        </div>
      )}

      <div className="detail-grid">
        <div className="detail-card">
          <h3 className="detail-card-title">Header</h3>
          <div className="detail-rows">
            <DetailRow label="OPCO" value={receipt.opcoRef} mono />
            <DetailRow label="Received by" value={receipt.receivedBy} mono />
            <DetailRow label="Received at" value={formatDateTime(receipt.receivedAt)} mono />
            <DetailRow label="Notes" value={receipt.notes ?? '—'} />
            <DetailRow label="Created at" value={formatDateTime(receipt.createdAt)} mono />
          </div>
        </div>

        <div className="detail-card">
          <h3 className="detail-card-title">
            Attachments ({receipt.attachments.length})
          </h3>
          {receipt.attachments.length === 0 ? (
            <p className="cell-secondary">
              {receipt.kind === 'SERVICE'
                ? 'No attachments on file (unexpected — SERVICE receipts require ≥1).'
                : 'None.'}
            </p>
          ) : (
            <ul className="detail-rows" style={{ listStyle: 'none', padding: 0 }}>
              {receipt.attachments.map((a, idx) => {
                const downloadHref = receiptAttachmentDownloadUrl(a.ref);
                return (
                  <li key={`${a.ref}-${idx}`} className="detail-row">
                    <span className="detail-row-label">
                      {downloadHref ? (
                        <a
                          href={downloadHref}
                          target="_blank"
                          rel="noopener noreferrer"
                          title="Open in a new tab"
                        >
                          {a.filename}
                        </a>
                      ) : (
                        a.filename
                      )}
                    </span>
                    <span className="detail-row-value">
                      <span className="cell-secondary mono">{a.ref}</span>
                      {a.mimeType && (
                        <>
                          {' '}
                          · <span className="cell-secondary">{a.mimeType}</span>
                        </>
                      )}
                      {a.sizeBytes != null && (
                        <>
                          {' '}
                          · <span className="cell-secondary mono">{formatBytes(a.sizeBytes)}</span>
                        </>
                      )}
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>

      <div className="po-lines">
        <div className="po-lines-header">
          <span className="form-label">Lines ({receipt.lines.length})</span>
        </div>
        <table className="po-lines-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Receipt line ref</th>
              <th>PO line</th>
              <th>Received qty</th>
            </tr>
          </thead>
          <tbody>
            {receipt.lines.map((line, idx) => (
              <tr key={line.receiptLineRef}>
                <td className="mono">{idx + 1}</td>
                <td className="mono">{line.receiptLineRef.slice(0, 24)}…</td>
                <td className="mono">{line.poLineRef.slice(0, 24)}…</td>
                <td className="mono">{line.receivedQuantity}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {reverseOpen && (
        <ReverseReceiptDialog
          receipt={receipt}
          onClose={() => setReverseOpen(false)}
          onDone={() => {
            setReverseOpen(false);
            void load();
          }}
          onError={(message) => toast.push(message, 'error')}
        />
      )}
    </section>
  );
}

interface ReverseReceiptDialogProps {
  readonly receipt: ReceiptRow;
  readonly onClose: () => void;
  readonly onDone: () => void;
  readonly onError: (message: string) => void;
}

function ReverseReceiptDialog({
  receipt,
  onClose,
  onDone,
  onError,
}: ReverseReceiptDialogProps): JSX.Element {
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const formRef = useRef<HTMLFormElement>(null);

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (reason.trim() === '') {
      setError('Reason is required');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const result = await reverseGoodsReceipt({
        receiptRef: receipt.receiptRef,
        reason: reason.trim(),
        requestingUser: REQUESTING_USER,
      });
      if (result.__typename === 'ReverseGoodsReceiptFailure') {
        const msg = `${result.class}: ${JSON.stringify(result.details)}`;
        setError(msg);
        onError(msg);
        setSubmitting(false);
        return;
      }
      onDone();
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setError(msg);
      onError(msg);
      setSubmitting(false);
    }
  };

  return (
    <SidePanel
      open
      title="Reverse goods receipt"
      width={640}
      onClose={onClose}
      onSave={() => formRef.current?.requestSubmit()}
      dirty={!submitting && reason.trim() !== ''}
      saveLabel={submitting ? 'Reversing…' : 'Reverse receipt'}
    >
      <form ref={formRef} className="form-grid" onSubmit={onSubmit}>
        <p className="t-body-sm">
          Reversing this receipt backs its quantities out of the PO line's
          received-to-date and stamps the audit columns. The receipt row stays
          for audit. Blocked if any AP invoice already references the PO lines —
          reverse / cancel the invoice first.
        </p>
        <label className="form-row form-row-full">
          <span className="form-label">Reason</span>
          <input
            type="text"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            required
            placeholder="Captured in the receipt's audit row"
          />
        </label>
        {error && (
          <div className="alert alert-neg" role="alert">
            {error}
          </div>
        )}
      </form>
    </SidePanel>
  );
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

